import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { handlePostRequest } from '../../utils/api-handler.util'
import { showToaster } from '../../utils/show-toaster.util'
import { OTP_PURPOSE_FORGOT_PASSWORD, MAX_OTP_LENGTH, OTP_TIMER_SECONDS } from '../../constants/otp'
import OTPInput from '../../components/auth/OTPInput'
import BackIcon from '../../components/icons/BackIcon'
import moment from 'moment'
import { useHistory } from '../../contexts/HistoryContext'

export default function ForgotPasswordOtpVerification() {
  const { state } = useLocation()
  const phoneNumber = state?.phoneNumber
  const [otp, setOtp] = useState(''.padEnd(MAX_OTP_LENGTH, ''))
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const { goBackOrHome } = useHistory()

  const timerEnd = useRef(moment().add(OTP_TIMER_SECONDS, 'seconds'))
  const [secondsLeft, setSecondsLeft] = useState(OTP_TIMER_SECONDS)

  const sendOtpMutation = useMutation({
    mutationFn: () =>
      handlePostRequest('/otp/generate', {
        phoneNumber,
        purpose: OTP_PURPOSE_FORGOT_PASSWORD,
      }),
    onSuccess: () => {
      showToaster('success', 'OTP sent!')
      timerEnd.current = moment().add(OTP_TIMER_SECONDS, 'seconds')
      setSecondsLeft(OTP_TIMER_SECONDS)
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to send OTP'
      setError(errorMessage)
    },
  })

  const verifyOtpMutation = useMutation({
    mutationFn: ({ phoneNumber, code }) => {
      return handlePostRequest('/otp/verify', {
        phoneNumber,
        otpCode: code,
        purpose: OTP_PURPOSE_FORGOT_PASSWORD,
      })
    },
    onSuccess: (data) => {
      showToaster('success', 'OTP verified!')
      navigate('/forgot-password/reset', {
        state: {
          phoneNumber,
          otp: data.data.otp.otpCode,
        },
      })
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Invalid OTP'
      setError(errorMessage)
    },
  })

  useEffect(() => {
    const interval = setInterval(() => {
      const diff = timerEnd.current.diff(moment(), 'seconds')
      setSecondsLeft(diff > 0 ? diff : 0)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const handleOtpChange = (otpString) => {
    setOtp(otpString)
    setError('')
  }

  const handleOtpComplete = (otpString) => {
    if (otpString.length !== MAX_OTP_LENGTH) {
      setError(`Please enter the ${MAX_OTP_LENGTH}-digit code.`)
      return
    }
    verifyOtpMutation.mutate({ phoneNumber, code: otpString })
  }

  const handleResend = () => {
    if (secondsLeft === 0 && phoneNumber) {
      sendOtpMutation.mutate()
      setError('')
    }
  }

  const formatTimer = () => {
    const m = String(Math.floor(secondsLeft / 60)).padStart(2, '0')
    const s = String(secondsLeft % 60).padStart(2, '0')
    return `${m}:${s}`
  }

  return (
    <div className="w-full max-w-md mx-auto min-h-screen  bg-background shadow-lg rounded-xl overflow-hidden">
      <div className="flex items-center p-4">
        <button
          className="flex items-center justify-center bg-background border border-gray-200 rounded-full w-10 h-10 shadow-sm hover:bg-neutral-200-50 transition-colors duration-200"
          onClick={() => goBackOrHome()}
          aria-label="Back"
        >
          <BackIcon />
        </button>
      </div>

      <div className="flex justify-center py-8">
        <img src="/eastsoko-logo.png" alt="Sokooni Logo" className="w-36 object-contain" />
      </div>

      <div className="px-6 pb-8">
        <h1 className="text-2xl font-bold text-foreground text-center font-inter mb-2">
          OTP Verification
        </h1>
        <p className="text-sm font-medium text-medium-opacity text-center font-inter mb-8">
          Enter the four digit code
        </p>

        <div className="flex justify-center mb-8">
          <OTPInput
            length={MAX_OTP_LENGTH}
            onChange={handleOtpChange}
            onComplete={handleOtpComplete}
          />
        </div>

        {error && (
          <div className="text-error-900 text-sm font-medium text-center mb-6">{error}</div>
        )}

        <button
          className="w-full py-3 bg-gradient-to-r from-secondary-700 to-secondary-500 text-white text-lg font-medium font-inter rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 disabled:opacity-70 mb-8"
          onClick={() => handleOtpComplete(otp)}
          disabled={verifyOtpMutation.isPending}
        >
          {verifyOtpMutation.isPending ? 'Verifying...' : 'Verify'}
        </button>

        <div className="text-center">
          <span className="text-foreground-muted font-inter">
            Resend:{' '}
            <button
              className="text-tertiary-700 font-bold hover:underline disabled:no-underline disabled:opacity-50"
              onClick={handleResend}
              disabled={secondsLeft > 0 || sendOtpMutation.isPending}
            >
              {formatTimer()}
            </button>
          </span>
        </div>
      </div>
    </div>
  )
}
